import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

interface AdminUser {
    id: string;
    name: string;
    email: string;
    initials: string;
    picture?: string;
}

@Component({
    selector: 'app-assignee-selector',
    templateUrl: './assignee-selector.component.html',
    styleUrls: ['./assignee-selector.component.scss'],
})
export class AssigneeSelectorComponent implements OnInit, OnDestroy, OnChanges {
    @Input() isVisible = false;

    public onChange: (x?: any) => void = () => {};

    public onTouched: (x?: any) => void = () => {};

    @Input() selectedAssigneeId: string;

    @Input() multiSelect = false;

    @Input() isInvalid = false;

    @Input() selectedAssigneeIds: string[] = [];

    @Input() preloadedAdminData: AdminUser[] = [];

    @Output() assigneeSelect = new EventEmitter<AdminUser>();

    @Output() assigneesSelect = new EventEmitter<AdminUser[]>();

    @Output() close = new EventEmitter<void>();

    admins: AdminUser[] = [];

    filteredAdmins: AdminUser[] = [];

    selectedAdmins: AdminUser[] = [];

    cachedAdmins: { [query: string]: AdminUser[] } = {}; // Cache for search results

    lastSearchQuery = ''; // Track the last search query

    searchQuery = '';

    searchSubject = new Subject<string>();

    subscription: Subscription;

    loading = false;

    hasInitialData = false; // Track if we've loaded initial data

    constructor(
        private elementRef: ElementRef,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.subscription = this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                // Only trigger a search if the query has changed and is not empty
                // or if it's empty but we haven't loaded initial data yet
                filter((query) => (query !== this.lastSearchQuery && query.trim() !== '') || (query.trim() === '' && !this.hasInitialData)),
            )
            .subscribe((query) => {
                this.searchAdmins(query);
            });

        if (this.isVisible) {
            this.loadInitialData();
        }

        // Process preloaded admin data if available
        if (this.preloadedAdminData && this.preloadedAdminData.length > 0) {
            this.processPreloadedAdminData();
        } else if (this.selectedAssigneeIds.length > 0) {
            this.initializeSelectedAdmins();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Check if selectedAssigneeIds has changed
        if (changes.selectedAssigneeIds && !changes.selectedAssigneeIds.firstChange) {
            const currentIds = changes.selectedAssigneeIds.currentValue || [];
            const previousIds = changes.selectedAssigneeIds.previousValue || [];

            // Check if the arrays are different
            if (this.arraysAreDifferent(currentIds, previousIds)) {
                this.initializeSelectedAdmins();
            }
        }
    }

    // Helper method to check if arrays have different contents
    private arraysAreDifferent(arr1: string[], arr2: string[]): boolean {
        if (arr1.length !== arr2.length) return true;

        // Create sorted copies to compare
        const sorted1 = [...arr1].sort();
        const sorted2 = [...arr2].sort();

        return sorted1.some((value, index) => value !== sorted2[index]);
    }

    // New method to process preloaded admin data
    private processPreloadedAdminData(): void {
        if (!this.preloadedAdminData || this.preloadedAdminData.length === 0) {
            return;
        }

        // Cache the preloaded admin data
        if (!this.cachedAdmins['']) {
            this.cachedAdmins[''] = [...this.preloadedAdminData];
        }

        // Update the admins list with preloaded data
        this.admins = [...this.preloadedAdminData];
        this.hasInitialData = true;

        // Find the selected admins from the preloaded data
        if (this.selectedAssigneeIds && this.selectedAssigneeIds.length > 0) {
            this.selectedAdmins = this.preloadedAdminData.filter((admin) => this.selectedAssigneeIds.includes(admin.id));
        }

        // Update filtered admins
        this.updateAdminsAfterSelection(this.admins);
    }

    // Method to initialize selectedAdmins based on selectedAssigneeIds
    private initializeSelectedAdmins(): void {
        // If we have preloaded admin data, use that instead
        if (this.preloadedAdminData && this.preloadedAdminData.length > 0) {
            this.processPreloadedAdminData();
            return;
        }

        if (!this.selectedAssigneeIds || this.selectedAssigneeIds.length === 0) {
            this.selectedAdmins = [];
            return;
        }

        // Check existing selectedAdmins first to avoid duplicates
        const existingIds = this.selectedAdmins.map((admin) => admin.id);
        const missingIds = this.selectedAssigneeIds.filter((id) => !existingIds.includes(id));

        if (missingIds.length === 0) {
            console.log('All selected assignees are already loaded');
            return;
        }

        // We'll create placeholder admin objects first
        missingIds.forEach((id) => {
            if (!this.selectedAdmins.some((a) => a.id === id)) {
                this.selectedAdmins.push({
                    id,
                    name: `Admin (${id})`,
                    email: '',
                    initials: 'AD',
                });
            }
        });

        // Load initial data if it hasn't been loaded yet
        if (!this.hasInitialData) {
            this.loadInitialData();
        } else {
            // Ensure the selected admins are included in filtered admins
            this.updateAdminsAfterSelection(this.admins);
        }

        // Then try to load the actual admin details through the search function
        this.loading = true;
        // Use empty search query to get all admins
        this.userService.searchAdmins('', 100).subscribe({
            next: (admins) => {
                // Find the actual admin objects that match our IDs
                const matchingAdmins = admins.filter((admin) => {
                    const adminId = admin.id || admin._id;
                    return this.selectedAssigneeIds.includes(adminId);
                });

                // Update our placeholder objects with real data
                matchingAdmins.forEach((admin) => {
                    const adminId = admin.id || admin._id;
                    const existingIndex = this.selectedAdmins.findIndex((a) => a.id === adminId);

                    if (existingIndex >= 0) {
                        // Replace placeholder with real data
                        this.selectedAdmins[existingIndex] = {
                            id: adminId,
                            name: admin.name || `${admin.firstName} ${admin.lastName}`,
                            email: admin.email,
                            picture: admin.picture,
                            initials: this.getInitials(admin.name || `${admin.firstName} ${admin.lastName}`),
                        };
                    } else {
                        // Add if not already there
                        this.selectedAdmins.push({
                            id: adminId,
                            name: admin.name || `${admin.firstName} ${admin.lastName}`,
                            email: admin.email,
                            picture: admin.picture,
                            initials: this.getInitials(admin.name || `${admin.firstName} ${admin.lastName}`),
                        });
                    }
                });

                console.log('Updated selectedAdmins with real data:', this.selectedAdmins);
                this.loading = false;

                // Cache these admins
                if (!this.cachedAdmins['']) {
                    this.cachedAdmins[''] = admins.map((admin) => ({
                        id: admin.id || admin._id,
                        name: admin.name || `${admin.firstName} ${admin.lastName}`,
                        email: admin.email,
                        picture: admin.picture,
                        initials: this.getInitials(admin.name || `${admin.firstName} ${admin.lastName}`),
                    }));
                }

                // Update the filtered admins to include these selected admins
                this.updateAdminsAfterSelection(this.admins.length > 0 ? this.admins : this.cachedAdmins[''] || []);
            },
            error: (error) => {
                console.error('Error fetching admin details:', error);
                this.loading = false;
                // Continue with placeholder data
            },
        });
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: MouseEvent): void {
        const clickedInside = this.elementRef.nativeElement.contains(event.target);
        if (!clickedInside && this.isVisible) {
            this.isVisible = false; // Directly close the dropdown
            this.close.emit();
        }
    }

    onSearchChange(query: string): void {
        this.searchQuery = query;
        this.searchSubject.next(query);

        // If search query is empty and we have cached data, use the empty query cache
        if (query.trim() === '' && this.cachedAdmins[''] && this.hasInitialData) {
            this.updateFilteredAdmins(this.cachedAdmins['']);
        }

        // If the dropdown is not visible, show it when typing
        if (!this.isVisible && query.trim().length > 0) {
            this.showDropdown();
        }
    }

    showDropdown(): void {
        this.isVisible = true;
        // Only load data if we don't have initial data yet
        if (!this.hasInitialData && this.admins.length === 0) {
            this.loadInitialData();
        }
    }

    loadInitialData(): void {
        // Check if we already have cached empty query results
        if (this.cachedAdmins['']) {
            this.updateFilteredAdmins(this.cachedAdmins['']);
            return;
        }

        // Load initial data (empty search)
        this.searchAdmins('');
    }

    toggleDropdown(): void {
        // Toggle visibility state
        this.isVisible = !this.isVisible;

        if (this.isVisible) {
            // If opening the dropdown, load data if needed
            if (!this.hasInitialData) {
                this.loadInitialData();
            }
        } else {
            // If closing, emit close event to notify parent components
            this.close.emit();
        }
    }

    searchAdmins(query: string): void {
        // Check if we have cached results for this query
        if (this.cachedAdmins[query]) {
            this.updateFilteredAdmins(this.cachedAdmins[query]);
            return;
        }

        this.loading = true;
        this.lastSearchQuery = query;

        this.userService.searchAdmins(query, 10).subscribe({
            next: (data) => {
                // Map results to AdminUser format
                const searchResults = data.map((admin) => ({
                    id: admin.id || admin._id,
                    name: admin.name || `${admin.firstName} ${admin.lastName}`,
                    email: admin.email,
                    picture: admin.picture,
                    initials: this.getInitials(admin.name || `${admin.firstName} ${admin.lastName}`),
                }));

                // Store results in cache
                this.cachedAdmins[query] = searchResults;

                // If this is an empty query, mark that we have initial data
                if (query.trim() === '') {
                    this.hasInitialData = true;
                }

                // Update the filtered admins
                this.updateFilteredAdmins(searchResults);
            },
            error: (error) => {
                console.error('Error fetching admins:', error);
                this.admins = [];
                this.filteredAdmins = [];
                this.loading = false;
            },
        });
    }

    updateFilteredAdmins(searchResults: AdminUser[]): void {
        // Store all admins found from search
        this.admins = searchResults;

        // For filtered admins, ensure selected admins are always included
        if (this.multiSelect && this.selectedAdmins.length > 0) {
            // Get IDs that are in the search results
            const searchResultIds = searchResults.map((admin) => admin.id);

            // Find selected admins that are not in search results
            const missingSelectedAdmins = this.selectedAdmins.filter((admin) => !searchResultIds.includes(admin.id));

            // Remove any selected admins from the search results to avoid duplicates
            const nonSelectedSearchResults = searchResults.filter((admin) => !this.selectedAssigneeIds.includes(admin.id));

            // Get selected admins that are in the search results
            const selectedSearchResults = searchResults.filter((admin) => this.selectedAssigneeIds.includes(admin.id));

            // Combine non-selected search results first, then all selected admins
            // This ensures all selected items appear after search results
            this.filteredAdmins = [...nonSelectedSearchResults, ...selectedSearchResults, ...missingSelectedAdmins];
        } else {
            this.filteredAdmins = [...searchResults];
        }

        // Identify selected admins from the fetched data
        if (this.multiSelect && this.selectedAssigneeIds.length > 0) {
            // Find any newly available admins that should be selected
            const newlyFoundSelectedAdmins = searchResults.filter((admin) => this.selectedAssigneeIds.includes(admin.id) && !this.selectedAdmins.some((selected) => selected.id === admin.id));

            // Add newly found selected admins to selectedAdmins array
            if (newlyFoundSelectedAdmins.length > 0) {
                this.selectedAdmins = [...this.selectedAdmins, ...newlyFoundSelectedAdmins];
            }
        }

        this.loading = false;
    }

    onAssigneeSelect(admin: AdminUser): void {
        if (this.multiSelect) {
            this.toggleAdminSelection(admin);
            // Don't clear the search text - keep current search query

            // Keep focus on the input to continue searching
            const inputElement = document.querySelector('.assignee-input') as HTMLInputElement;
            if (inputElement) {
                inputElement.focus();
            }

            // Use the current search results to maintain filtering context
            // If there's an active search query, use that cache
            if (this.searchQuery.trim() !== '' && this.cachedAdmins[this.searchQuery]) {
                this.updateAdminsAfterSelection(this.cachedAdmins[this.searchQuery]);
            } else if (this.cachedAdmins['']) {
                this.updateAdminsAfterSelection(this.cachedAdmins['']);
            } else {
                this.updateAdminsAfterSelection(this.admins);
            }
        } else {
            this.assigneeSelect.emit(admin);
            this.searchQuery = admin.name; // Set the input field to the selected admin's name
            this.selectedAssigneeId = admin.id; // Update the selected ID
            this.isVisible = false; // Close the dropdown after selection
        }
    }

    updateAdminsAfterSelection(adminsList: AdminUser[]): void {
        if (this.selectedAdmins.length > 0) {
            // Get IDs of admins in the list
            const currentAdminIds = adminsList.map((a) => a.id);

            // Find selected admins that aren't in the list
            const missingSelectedAdmins = this.selectedAdmins.filter((selectedAdmin) => !currentAdminIds.includes(selectedAdmin.id));

            // Remove any selected admins from the admin list to avoid duplicates
            const nonSelectedAdmins = adminsList.filter((admin) => !this.selectedAssigneeIds.includes(admin.id));

            // Get selected admins that are in the admin list
            const selectedAdminsInList = adminsList.filter((admin) => this.selectedAssigneeIds.includes(admin.id));

            // Combine non-selected admins first, then all selected admins
            // This ensures all selected items appear after non-selected items
            this.filteredAdmins = [...nonSelectedAdmins, ...selectedAdminsInList, ...missingSelectedAdmins];
        } else {
            // If there are no selected admins, just show all available admins
            this.filteredAdmins = [...adminsList];
        }
    }

    toggleAdminSelection(admin: AdminUser): void {
        const adminIndex = this.selectedAdmins.findIndex((a) => a.id === admin.id);

        if (adminIndex === -1) {
            // Admin not selected, add to selection
            this.selectedAdmins.push(admin);
            this.selectedAssigneeIds.push(admin.id);
        } else {
            // Admin already selected, remove from selection
            this.selectedAdmins.splice(adminIndex, 1);
            const idIndex = this.selectedAssigneeIds.findIndex((id) => id === admin.id);
            if (idIndex !== -1) {
                this.selectedAssigneeIds.splice(idIndex, 1);
            }
        }

        // Emit the updated selection
        this.assigneesSelect.emit([...this.selectedAdmins]);
    }

    onSearchFocus(): void {
        // Only show dropdown if not already visible and not being toggled closed
        if (!this.isVisible) {
            this.showDropdown();
        }
    }

    isAdminSelected(adminId: string): boolean {
        if (this.multiSelect) {
            return this.selectedAssigneeIds.includes(adminId);
        }
        return this.selectedAssigneeId === adminId;
    }

    getInitials(name: string): string {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    }
}
